// AI-CONTEXT: See docs/features/impact-in-beeld-hub.md
// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import { useMemo } from 'react'
import { useFirestoreLoaded } from '../firebase/useFirestore'
import useParticipationPeriodValue from './useParticipationPeriodValue'
import useMsiMonetaryTotal from './useMsiMonetaryTotal'
import useMaterialValue from './useMaterialValue'
import { getLatestCosts } from '../helpers/dataHelpers/impactPerEuro'
import computeImpactPerEuro from '../helpers/impactPerEuroMethodiek/computeImpactPerEuro'
import composeTeller from '../helpers/impactPerEuroMethodiek/composeTeller'
import METHODIEK_VERSIE from '../helpers/impactPerEuroMethodiek/methodiekVersie'
import dataSourceErrorMessage from '../helpers/dataSourceErrorMessage'

/**
 * Impact per euro volgens methodiek v1.1 — de ENIGE bron voor dit kengetal in
 * de frontend: DashboardImpactPerEuroCard én de Impact in beeld-hub consumeren
 * deze hook, zodat beide altijd hetzelfde getal tonen. Boekjaar = nieuwste
 * CompagnyCosts-rij; noemer + status via computeImpactPerEuro.
 *
 * De teller telt DRIE waardestromen op (methodiek § 1.1):
 *   1. participatieladder-periode-delta, toegerekend aan de interventie
 *   2. MSI-periode-delta, toegerekend aan de interventie
 *   3. maatschappelijke besparing van materialen en coachadviezen
 *
 * Sinds methodiek v1.2 gebruiken stroom 1 en 2 de toegerekende waarde van de
 * MSI-rekenregels (impactclaim, tijdshorizon, drop-off, disconto), niet het
 * bruto jaarbedrag. Wie geen duur of uren heeft, telt niet mee en staat in
 * `notAttributedCount` (dekkingsindicator, § 6.3).
 * AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md
 *
 * Van die derde stroom telt alleen de maatschappelijke besparing mee. De
 * besparing op de energierekening en de bespaarde CO2 zijn outputs met een
 * eigen eenheid en horen in de benchmark (§ 1.3).
 *
 * @returns {{
 *   ratio: number|null,
 *   totalValue: number|null,
 *   result: import('../helpers/impactPerEuroMethodiek/computeImpactPerEuro').KengetalResultaat|null,
 *   label: string|null,
 *   loading: boolean,
 *   materialUnknownCount: number,
 *   notAttributedCount: number|null,
 *   notAttributedReasons: Object<string, number>,
 *   error: Error|null,
 *   errorMessage: string|null,
 * }} loading = de stromen zijn nog onderweg (te onderscheiden van 'geen
 *   kengetal', dat een uitkomst is); ratio = presenteerbaar kengetal (€ 0,05-afronding, status-gated) of null;
 *   totalValue = boekjaar-gescopete maatschappelijke waarde, null tijdens laden;
 *   result = volledige methodiek-uitkomst (status/reden/jaar) voor consumers
 *   die meer context tonen; materialUnknownCount = registraties buiten het
 *   totaal; errorMessage = de foutmelding voor het scherm, met de naam van de
 *   stroom die wegviel — toon die, verzin er geen eigen tekst bij.
 *   Callers leggen een null uit, nooit raden.
 */
/**
 * @param {...(Object<string, number>|undefined)} lists
 * @returns {Object<string, number>}
 */
const mergeReasons = (...lists) => {
    const merged = {}
    lists.forEach((reasons) => {
        Object.entries(reasons || {}).forEach(([reason, count]) => { merged[reason] = (merged[reason] || 0) + count })
    })
    return merged
}

const useImpactPerEuroRatio = () => {
    // Loaded-vlag i.p.v. kale array: de MSI-fetch mag pas starten als de
    // kostenrijen (en dus de jaargrenzen) er echt zijn — anders vertrekt eerst
    // een all-time sweep die ms later door de echte grenzen wordt weggegooid.
    const [compagnyCosts, costsLoaded] = useFirestoreLoaded('CompagnyCosts')

    // Rapportagejaar = de nieuwste kostenrij (zelfde keuze als de dashboard-kaart).
    const latest = getLatestCosts(Array.isArray(compagnyCosts) ? compagnyCosts : [])
    const jaar = latest ? Number(latest.Year) : null

    const startMs = jaar !== null ? new Date(jaar, 0, 1).getTime() : undefined
    const endMs = jaar !== null ? new Date(jaar + 1, 0, 1).getTime() - 1 : undefined

    const {
        attributedValue: participationValue,
        notAttributedCount: participationNotAttributed,
        notAttributedReasons: participationNotAttributedReasons,
        linkedMeasurementCount: participationCount,
        loading: participationLoading,
        error: parametersError,
    } = useParticipationPeriodValue(jaar)
    const {
        attributedTotal: msiTotal,
        notAttributedCount: msiNotAttributed,
        notAttributedReasons: msiNotAttributedReasons,
        linkedPersonaDeltaCount,
        loading: msiLoading,
        error: msiError,
    } = useMsiMonetaryTotal({ startMs, endMs, enabled: costsLoaded })
    const {
        value: materialValue,
        loading: materialLoading,
        unknownCount: materialUnknownCount,
        error: materialError,
    } = useMaterialValue({ startMs, endMs })

    // 'Nog niet bekend' is een eigen toestand, geen null-kengetal: een
    // organisatie zonder kostenrij heeft GEEN kengetal (uitkomst, streepje),
    // een organisatie waarvan de stromen nog binnenkomen heeft er nog geen
    // (laadindicator). Zonder dit onderscheid beweert de tegel een afwezigheid
    // die er niet is.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const loading = !costsLoaded
        || participationLoading === true
        || msiLoading === true
        || materialLoading === true

    const result = useMemo(() => {
        if (!latest) return null

        const { teller, linkedMeasurementCount } = composeTeller({
            participationValue,
            participationCount,
            msiValue: msiTotal,
            msiCount: linkedPersonaDeltaCount,
            materialValue,
        })
        if (teller === null) return null

        return computeImpactPerEuro({ teller, costsRow: latest, linkedMeasurementCount })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latest, participationValue, msiTotal, materialValue, participationCount, linkedPersonaDeltaCount])

    const { teller: totalValue } = composeTeller({
        participationValue,
        msiValue: msiTotal,
        materialValue,
    })

    const label = result && result.kengetal !== null
        ? `v${METHODIEK_VERSIE} · ${result.status} · ${result.jaar}`
        : null

    return {
        ratio: result?.kengetal ?? null,
        totalValue,
        result,
        label,
        loading,
        // Hoeveel materiaalregistraties niet konden worden doorgerekend. Hoort
        // bij de dekkingsindicator (§ 6.3): een totaal dat stilzwijgend een deel
        // weglaat ziet eruit als een volledige meting.
        materialUnknownCount,
        // Deelnemers en respondenten zonder duur of uren: hun verschil zit niet in
        // de teller. Zelfde reden als hierboven — een stil weggelaten deel ziet
        // eruit als een volledige meting.
        notAttributedCount: participationNotAttributed === null || msiNotAttributed === null
            ? null
            : participationNotAttributed + msiNotAttributed,
        // Per reden, voor dezelfde zin als op de detailpagina (notAttributedText).
        notAttributedReasons: mergeReasons(participationNotAttributedReasons, msiNotAttributedReasons),
        error: parametersError || msiError || materialError || null,
        // De melding hoort bij de hook en niet bij elk scherm: de drie stromen
        // hebben elk hun eigen bron, en alleen hier is bekend welke wegviel.
        // Schermen die dit zelf invulden toonden alledrie 'Rekenparameters
        // niet geladen', ook als de rekenparameters gewoon binnen waren.
        errorMessage: dataSourceErrorMessage([
            ['rekenparameters', parametersError],
            ['effectwaarden', msiError],
            ['materiaalwaarden', materialError],
        ]),
    }
}

export default useImpactPerEuroRatio
